import React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BaseDiv } from '../../common/BaseElements';
import { GapContainer, SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';
import { IBorderRadiusProps, useBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { IGapProps, IGutterProps } from '../../fundamentals/spaces/space';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { CarouselVariant } from './carouselTypes';

export interface ICarouselPaginationProps {
  variant?: CarouselVariant;
  nested?: boolean;
  currentPage?: number;
  totalPages: number;
}

interface IDotProps extends Pick<ICarouselPaginationProps, 'variant' | 'nested'> {
  selected: boolean;
}

const containerStyleProps: IGapProps & IGutterProps & IBorderRadiusProps = {
  gutterBottom: 'small',
  gutterTop: 'small',
  gutterLeft: 'offgrid_6',
  gutterRight: 'offgrid_6',
  gap: 'offgrid_6',
  borderRadius: 'offgrid7'
};

export const carouselPaginationColours = ({ eoraCoreColours }: IColoursCollection) => ({
  carouselPaginationColours: {
    colour: eoraCoreColours.BASE.BLACK, // default selected dot colour
    opacity: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_20, // default dot colour
    dark: {
      colour: eoraCoreColours.BASE.WHITE,
      background: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_70,
      opacity: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_20
    },
    light: {
      colour: eoraCoreColours.BASE.BLACK,
      background: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_70,
      opacity: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_20
    }
  }
});

const getDotColour = (theme: DefaultTheme, { variant, selected, nested }: IDotProps): string => {
  const { colour, opacity } = theme.carouselPaginationColours[variant!];
  if (selected) return nested ? colour : theme.carouselPaginationColours.colour;
  return nested ? opacity : theme.carouselPaginationColours.opacity;
};

const CenteredContainer = styled(SpaceContainer)<{ absolute?: boolean }>`
  justify-content: center;
  display: flex;
  height: ${PX_TO_REM[14]};
  width: 100%;
  ${({ absolute }) =>
    absolute &&
    css`
      bottom: ${PX_TO_REM[16]};
      position: absolute;
    `}
`;

const CarouselPaginationContainer = styled(GapContainer)<Pick<ICarouselPaginationProps, 'variant' | 'nested'> & IBorderRadiusProps>`
  justify-content: center;
  align-items: center;
  ${useBorderRadiusCss()};
  ${({ nested, variant, theme }) =>
    nested &&
    css`
      background-color: ${theme.carouselPaginationColours[variant!].background};
    `}
`;

const Dot = styled(BaseDiv)<IDotProps>`
  width: ${PX_TO_REM[6]};
  height: ${PX_TO_REM[6]};
  border-radius: 50%;
  background-color: ${({ theme, ...rest }) => getDotColour(theme, { ...rest })};
`;

export const CarouselPagination: React.FC<ICarouselPaginationProps> = ({ nested = false, variant = 'light', currentPage = 0, totalPages, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'CarouselPagination');
  return (
    <CenteredContainer {...generateTestId()} absolute={nested}>
      <CarouselPaginationContainer {...containerStyleProps} nested={nested} variant={variant} {...generateTestId('InnerContainer')} {...rest}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <Dot key={index} {...generateTestId(`Dot.${index}`)} nested={nested} variant={variant} selected={index === currentPage} />
        ))}
      </CarouselPaginationContainer>
    </CenteredContainer>
  );
};
